import { FormEvent } from 'react'
import { useForm } from '@inertiajs/react'
import { Button } from '@/components/ui/Button'
import { InputField, SelectField } from '@/components/shared/form'
import {
  Dialog,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
  DialogClose,
} from '@/components/ui/Dialog'

export interface Member {
  id: string
  name: string | null
  email: string
  created_at: string
  role_id?: string | null
  role_name?: string
}

interface MemberFormData {
  email: string
  name: string
  password: string
  role_id: string
}

interface MemberFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  member?: Member | null
  roles: { id: string; name: string }[]
}

const selectClass =
  'flex h-9 w-full items-center justify-between rounded-md border border-border bg-bg px-3 py-1 text-sm focus:outline-none focus:border-accent'

function NativeSelect({
  id,
  value,
  onChange,
  options,
  includeBlank,
  required,
}: {
  id: string
  value: string
  onChange: (v: string) => void
  options: { id: string; name: string }[]
  includeBlank?: boolean
  required?: boolean
}) {
  return (
    <select id={id} className={selectClass} value={value} onChange={(e) => onChange(e.target.value)} required={required}>
      {includeBlank && <option value="">—</option>}
      {options.map((o) => (
        <option key={o.id} value={o.id}>
          {o.name}
        </option>
      ))}
    </select>
  )
}

function Field({
  label,
  htmlFor,
  error,
  hint,
  children,
}: {
  label: string
  htmlFor: string
  error?: string
  hint?: string
  children: React.ReactNode
}) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={htmlFor} className="text-sm font-medium">
        {label}
      </label>
      {children}
      {error ? (
        <p className="text-xs text-err">{error}</p>
      ) : hint ? (
        <p className="text-xs text-fg-muted">{hint}</p>
      ) : null}
    </div>
  )
}

export default function MemberFormDialog({ open, onOpenChange, member, roles }: MemberFormDialogProps) {
  const isEdit = Boolean(member)

  const form = useForm<MemberFormData>({
    email: member?.email ?? '',
    name: member?.name ?? '',
    password: '',
    role_id: member?.role_id ?? roles[0]?.id ?? '',
  })

  const submit = (e: FormEvent) => {
    e.preventDefault()
    form.transform((data) => ({ member: data }))
    const onSuccess = () => onOpenChange(false)

    if (isEdit && member) {
      form.put(`/members/${member.id}`, { onSuccess, preserveScroll: true })
    } else {
      form.post('/members', { onSuccess, preserveScroll: true })
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) form.clearErrors()
        onOpenChange(next)
      }}
    >
      <DialogHeader>
        <DialogTitle>{isEdit ? 'Edit member' : 'New member'}</DialogTitle>
        <DialogDescription>
          {isEdit ? 'Update this member’s details.' : 'Create a new user account.'}
        </DialogDescription>
      </DialogHeader>

      <form onSubmit={submit} className="space-y-4">
        <InputField
          form={form}
          name="email"
          label="Email"
          type="email"
          inputMode="email"
          spellCheck={false}
          required
        />

        <InputField form={form} name="name" label="Name" />

        <SelectField
          form={form}
          name="role_id"
          label="Role"
          options={roles.map((role) => ({ value: role.id, label: role.name }))}
          required
        />

        <InputField
          form={form}
          name="password"
          label="Password"
          type="password"
          autoComplete="new-password"
          required={!isEdit}
          hint={isEdit ? 'Leave blank to keep the current password.' : undefined}
        />

        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline">
              Cancel
            </Button>
          </DialogClose>
          <Button type="submit" disabled={form.processing}>
            {form.processing
              ? isEdit
                ? 'Saving…'
                : 'Creating…'
              : isEdit
                ? 'Save changes'
                : 'Create member'}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  )
}
